import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        // /api/auth/me returns User model directly (flat), not wrapped in {user: ...}
        if (response.data?.user_id) {
          // User is already logged in, redirect to dashboard or intended page
          const returnTo = searchParams.get('returnTo') || '/dashboard';
          navigate(returnTo);
        }
      } catch {
        // Not logged in, show login page
      } finally {
        setCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate, searchParams]);

  const handleGoogleLogin = () => {
    const returnTo = searchParams.get('returnTo') || '/dashboard';
    const redirectUrl = `${window.location.origin}${returnTo}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error(t('login.emailRequired', 'Please enter your email address'));
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('login.emailInvalid', 'Please enter a valid email address'));
      return;
    }

    setLoading(true);
    try {
      const returnTo = searchParams.get('returnTo') || '/dashboard';
      await axios.post(`${API}/auth/magic-link`, { 
        email,
        return_to: returnTo
      });
      setMagicLinkSent(true);
      toast.success(t('login.magicLinkSent', 'Magic link sent! Check your email.'));
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('login.magicLinkError', 'Failed to send magic link');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-rose-50/30 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back to home */}
        <Link 
          to="/" 
          className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t('common.backToHome', 'Back to Home')}
        </Link>

        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-2">
            <img 
              src="/logo-transparent.png" 
              alt="Even Rounds Logo" 
              className="h-20 w-20 object-contain"
            />
            <span className="text-3xl font-heading font-bold text-primary">Even Rounds</span>
          </Link>
          <p className="text-muted-foreground mt-2">
            {t('login.subtitle', 'Your yarn stash, organized.')}
          </p>
        </div>

        <div className="bg-white rounded-3xl border border-stone-100 p-8 shadow-sm">
          {magicLinkSent ? (
            /* Magic link sent confirmation */
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2">
                {t('login.checkEmail', 'Check your email')}
              </h1>
              <p className="text-muted-foreground mb-6">
                {t('login.magicLinkInstructions', 'We sent a magic link to')} <strong>{email}</strong>. {t('login.clickToLogin', 'Click the link to sign in.')}
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                {t('login.noEmail', "Didn't receive it?")}
              </p>
              <Button
                variant="outline"
                onClick={() => setMagicLinkSent(false)}
                className="rounded-full"
              >
                {t('login.tryAgain', 'Try again')}
              </Button>
            </div>
          ) : (
            /* Login options */
            <>
              <h1 className="text-2xl font-heading font-bold text-center mb-6">
                {t('login.title', 'Sign in')}
              </h1>

              {/* Google OAuth */}
              <Button
                onClick={handleGoogleLogin}
                className="w-full rounded-full bg-white border border-stone-200 text-stone-700 hover:bg-stone-50 mb-4"
                size="lg"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {t('login.continueWithGoogle', 'Continue with Google')}
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-stone-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-muted-foreground">
                    {t('login.or', 'or')}
                  </span>
                </div>
              </div>

              {/* Magic Link */}
              <form onSubmit={handleMagicLinkSubmit}>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                    {t('login.emailLabel', 'Email address')}
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder', 'you@example.com')}
                    className="rounded-xl"
                    disabled={loading}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full"
                  size="lg"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('login.sending', 'Sending...')}
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      {t('login.sendMagicLink', 'Send magic link')}
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-center text-muted-foreground mt-6">
                {t('login.noPassword', 'No password needed. We\'ll email you a secure link to sign in.')}
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('login.terms', 'By signing in, you agree to our')}{' '}
          <Link to="/terms" className="text-primary hover:underline">
            {t('login.termsLink', 'Terms of Service')}
          </Link>{' '}
          {t('login.and', 'and')}{' '}
          <Link to="/privacy" className="text-primary hover:underline">
            {t('login.privacyLink', 'Privacy Policy')}
          </Link>
        </p>
      </div>
    </div>
  );
}
